// KidsEditionIntro — kids-side parallel of Reminiscence101. Sits between
// the dementia/lavender science section and the TwoEndsDemos split, so
// both editions get an equivalent "header" before the visitor steps into
// the demo cards. Mirrors Reminiscence101's visual structure for continuity.
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Heart } from "@phosphor-icons/react";
import { ForgetMeNotIcon } from "../ForgetMeNotIcon";
import { FmnWatermark } from "../FmnWatermark";

const FADE = {
    initial: { opacity: 0, y: 22 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true, margin: "-80px" },
    transition: { duration: 0.9, ease: [0.16, 1, 0.3, 1] },
};

export function KidsEditionIntro() {
    const { t } = useTranslation();
    const items = t("kids_intro.tldr_items", { returnObjects: true });
    const list = Array.isArray(items) ? items : [];
    return (
        <section
            className="relative py-28 px-6 bg-gradient-to-b from-[#FBF1F2] via-white to-[#EAF2F9] overflow-hidden"
            id="kids-intro"
            data-testid="kids-edition-intro"
        >
            <FmnWatermark position="bottom-right" size={720} opacity={0.05} rotate={-14} />
            <div className="max-w-6xl mx-auto">
                <motion.div {...FADE} className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
                    <div className="lg:col-span-5">
                        <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#A85D62] font-semibold mb-6">
                            {t("kids_intro.eyebrow")}
                        </p>
                        <h2 className="font-serif text-4xl sm:text-5xl text-[#2D2B2A] leading-tight">
                            {t("kids_intro.headline_part1")}{" "}
                            <em className="italic text-[#A85D62]">
                                {t("kids_intro.headline_emphasis")}
                            </em>
                        </h2>
                    </div>

                    <div className="lg:col-span-7 space-y-5 text-[#5C5A59] text-lg leading-relaxed font-light max-w-prose">
                        <p>{t("kids_intro.p1")}</p>
                        <p>{t("kids_intro.p2")}</p>
                        <p>{t("kids_intro.p3")}</p>
                    </div>
                </motion.div>

                <motion.div
                    {...FADE}
                    transition={{ ...FADE.transition, delay: 0.15 }}
                    className="mt-14 rounded-[2rem] bg-white border border-[#F4C7CC]/40 shadow-rem-card p-8 lg:p-12"
                    data-testid="kids-intro-tldr"
                >
                    <p className="font-sans text-xs uppercase tracking-[0.3em] text-[#A85D62] font-semibold mb-6">
                        {t("kids_intro.tldr_eyebrow")}
                    </p>
                    <ul className="space-y-4 text-[#2D2B2A] leading-relaxed font-light">
                        {list.map((item, i) => (
                            <li
                                key={i}
                                className="flex items-start gap-3"
                                data-testid={`kids-intro-item-${i + 1}`}
                            >
                                <Heart size={18} weight="duotone" className="shrink-0 mt-1 text-[#A85D62]" />
                                <span>{item}</span>
                            </li>
                        ))}
                    </ul>
                    <div className="mt-8 pt-6 border-t border-[#F4C7CC]/40 flex items-center justify-center text-sm text-[#8A8786] italic gap-3">
                        <ForgetMeNotIcon size={18} />
                        Same software · same forget-me-not · only the case changes.
                    </div>
                </motion.div>
            </div>
        </section>
    );
}
